import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Swords, Trophy, Bomb } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { cn } from '@/lib/utils';

interface NFLMatchupCardProps {
  onNavigate?: (tab: string) => void;
  /** SAB-501: the league's current week (from useNFLLeague), so the card matches the Home hero. */
  currentWeek: number;
}

interface MatchupData {
  id: string;
  week: number;
  status: string;
  home_team_id: string;
  away_team_id: string;
  home_score: number;
  away_score: number;
  winner_team_id: string | null;
  // SAB-1183: the ONLY thing that makes a win a championship. Previously unfetched, so
  // the card could not consult it and celebrated every completed win as a title.
  is_championship: boolean | null;
}

interface SabotageInfo {
  attacking: number;
  defending: number;
}

export default function NFLMatchupCard({ onNavigate, currentWeek }: NFLMatchupCardProps) {
  const { currentTeam, currentLeague, teams } = useNFLLeague();
  const [matchup, setMatchup] = useState<MatchupData | null>(null);
  const [sabotages, setSabotages] = useState<SabotageInfo>({ attacking: 0, defending: 0 });

  useEffect(() => {
    if (!currentLeague || !currentTeam) return;

    async function load() {
      // SAB-501: key to the current week (matches the Home hero), NOT the last
      // scheduled week. A team has at most one matchup per week.
      const { data } = await supabase
        .from('nfl_matchups')
        .select('id, week, status, home_team_id, away_team_id, home_score, away_score, winner_team_id, is_championship')
        .eq('league_id', currentLeague!.id)
        .eq('week', currentWeek)
        .or(`home_team_id.eq.${currentTeam!.id},away_team_id.eq.${currentTeam!.id}`)
        .limit(1);

      if (data && data.length > 0) {
        setMatchup(data[0]);

        // Fetch active sabotage counts for this matchup
        const { data: events } = await (supabase as any)
          .from('nfl_sabotage_events')
          .select('attacker_team_id, target_team_id')
          .eq('matchup_id', data[0].id)
          .eq('status', 'active');

        if (events) {
          const attacking = events.filter((e: any) => e.attacker_team_id === currentTeam!.id).length;
          const defending = events.filter((e: any) => e.target_team_id === currentTeam!.id).length;
          setSabotages({ attacking, defending });
        }
      } else {
        // Current week has no matchup yet (offseason / not scheduled). Clear any
        // stale prior-week state so the graceful empty state renders.
        setMatchup(null);
        setSabotages({ attacking: 0, defending: 0 });
      }
    }

    load();
  }, [currentLeague, currentTeam, currentWeek]);

  const teamMap = Object.fromEntries(teams.map(t => [t.id, t]));

  if (!matchup) {
    return (
      <Card className="bg-card border-border cursor-pointer hover:border-primary/30 transition-all" onClick={() => onNavigate?.('matchups')}>
        <CardContent className="p-5">
          <div className="flex flex-col items-center justify-center py-6 text-muted-foreground text-sm text-center gap-1">
            <span>No matchup scheduled for Week {currentWeek} yet</span>
            <span className="text-xs text-muted-foreground/60">Check back once the week is set</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isHome = matchup.home_team_id === currentTeam?.id;
  const myScore = isHome ? matchup.home_score : matchup.away_score;
  const oppScore = isHome ? matchup.away_score : matchup.home_score;
  const oppTeamId = isHome ? matchup.away_team_id : matchup.home_team_id;
  const oppTeam = teamMap[oppTeamId];
  // SAB-1183: 'final' was a dead branch — nfl_matchups.status only ever holds
  // 'completed' or 'scheduled'. Matching a literal outside the column's vocabulary
  // reads as though two states are expected when only one exists.
  const isFinal = matchup.status === 'completed';
  const isWinner = matchup.winner_team_id === currentTeam?.id;
  const isChampionship = matchup.is_championship === true;

  return (
    <Card
      className="bg-card border-border cursor-pointer hover:border-primary/30 transition-colors overflow-hidden"
      onClick={() => onNavigate?.('matchups')}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold font-display text-foreground flex items-center gap-2">
            <Swords className="h-4 w-4 text-primary" />
            Your Matchup
          </h2>
          <Badge
            className={cn(
              'text-[10px] px-2 py-0.5 border',
              isFinal
                ? 'bg-muted/10 text-muted-foreground border-border/20'
                : 'bg-primary/10 text-primary border-primary/20'
            )}
          >
            {isFinal ? 'FINAL' : `WEEK ${matchup.week}`}
          </Badge>
        </div>

        <div className="flex items-center">
          {/* Your Team */}
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground truncate">{currentTeam?.name || 'Your Team'}</p>
            <p className={cn(
              'text-2xl font-bold font-display text-foreground',
              isFinal && isWinner && 'text-primary'
            )}>
              {Number(myScore).toFixed(1)}
            </p>
            <p className="text-xs text-primary">You</p>
          </div>

          {/* VS */}
          <div className="shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground mx-2">
            vs
          </div>

          {/* Opponent */}
          <div className="flex-1 min-w-0 text-right">
            <p className="text-xs text-muted-foreground truncate">{oppTeam?.name || 'Opponent'}</p>
            <p className={cn(
              'text-2xl font-bold font-display text-foreground',
              isFinal && !isWinner && 'text-primary'
            )}>
              {Number(oppScore).toFixed(1)}
            </p>
            <p className="text-xs text-muted-foreground">Opponent</p>
          </div>
        </div>

        {/* SAB-1183: the trophy is gated on is_championship, NOT on having won.
            It used to read `isFinal && isWinner`, which consulted nothing about the
            championship at all — so every completed win in every league announced a
            title. 141 matchups across 9 leagues were affected, including week 1 REG.

            The data was never wrong: is_championship is false on all 141. The card
            simply never fetched it.

            A plain win still gets an acknowledgement, just not the word Championship —
            silently dropping the banner would have left a won matchup with nothing. */}
        {isFinal && isWinner && isChampionship && (
          <div className="mt-3 flex items-center justify-center gap-1.5 text-amber-400">
            <Trophy className="h-4 w-4" />
            <span className="text-xs font-bold">Championship Winner!</span>
          </div>
        )}
        {isFinal && isWinner && !isChampionship && (
          <div className="mt-3 flex items-center justify-center gap-1.5 text-primary">
            <span className="text-xs font-bold">You won this matchup</span>
          </div>
        )}

        {(sabotages.attacking > 0 || sabotages.defending > 0) && (
          <div className="mt-3 flex items-center justify-center gap-2">
            {sabotages.attacking > 0 && (
              <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px] gap-1">
                <Bomb className="h-3 w-3" />
                {sabotages.attacking} sent
              </Badge>
            )}
            {sabotages.defending > 0 && (
              <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[10px] gap-1">
                <Bomb className="h-3 w-3" />
                {sabotages.defending} incoming
              </Badge>
            )}
          </div>
        )}

        <div className="mt-3 text-center text-xs text-primary">View full matchup →</div>
      </CardContent>
    </Card>
  );
}
